import {
  memo,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Box, useTheme } from '@mui/material';
import {
  default as Map,
  Layer,
  MapRef,
  Source,
  ViewState,
  ViewStateChangeEvent,
} from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

import ComparisonMapLegend from './ComparisonMapLegend';
import ComparisonMapOverlay from './ComparisonMapOverlay';
import ComparisonMapTooltip from './ComparisonMapTooltip';
import {
  COMPARISON_MAP_CONTAINER_ID,
  COMPARISON_MAP_SOURCE_PREFIX,
} from './useHeatMapSync';

import type { PriorityGroup } from '@/features/offlineSalesOptimizer/types';
import type {
  GeoJSONFeature,
  MapEvent,
  MapSourceDataEvent,
  VectorTileSource,
} from 'mapbox-gl';

import { useColorTokens } from '@/app/themes';
import {
  mapboxAccessToken,
  mapboxMapStylesLinks,
} from '@/features/offlineSalesOptimizer/common';
import MapControls from '@/features/offlineSalesOptimizer/components/mapControls/MapControls';

export interface ComparisonMapItem {
  areaId: string;
  areaName: string;
  bucket: PriorityGroup | null;
  populationSize: number;
}

interface ComparisonMapBodyProps {
  mapIndex: 0 | 1;
  tileInfo: { mapboxTileId: string | null; sourceLayer: string | null };
  viewState: ViewState;
  activeMap: number;
  mapsRefs: RefObject<(MapRef | null)[]>;
  onMove: (evt: ViewStateChangeEvent) => void;
  onMoveStart: (index: number) => void;
  throttledOnHover: (...args: unknown[]) => void;
  removeHoverInfo: () => void;
  hasCursor: boolean;
  bucketColorExpr: string;
  onMapLoaded: (loaded: boolean) => void;
  onLayerLoaded: (loaded: boolean) => void;
}

const ComparisonMapBody = memo(
  ({
    mapIndex,
    tileInfo,
    viewState,
    activeMap,
    mapsRefs,
    onMove,
    onMoveStart,
    throttledOnHover,
    removeHoverInfo,
    hasCursor,
    bucketColorExpr,
    onMapLoaded,
    onLayerLoaded,
  }: ComparisonMapBodyProps) => {
    const theme = useTheme();

    const sourceId = `${COMPARISON_MAP_SOURCE_PREFIX}${mapIndex.toString()}`;
    const areaFillLayerId = `comparison-map-fill-${mapIndex.toString()}`;
    const { mapboxTileId, sourceLayer } = tileInfo;

    const handleLoad = useCallback(
      (e: MapEvent) => {
        const map = e.target;
        onMapLoaded(true);

        const fitFirstMapToSourceBounds = () => {
          const source = map.getSource(sourceId) as VectorTileSource | null;
          if (mapIndex === 0 && source?.bounds) {
            map.fitBounds(source.bounds, { padding: 20, essential: true });
          }
        };

        const onSourceLoaded = (evt: MapSourceDataEvent) => {
          if (
            evt.sourceId === sourceId &&
            evt.isSourceLoaded &&
            evt.sourceDataType === 'metadata'
          ) {
            onLayerLoaded(true);
            fitFirstMapToSourceBounds();
          }
        };

        map.on('sourcedata', onSourceLoaded);

        if (map.getSource(sourceId) && map.isSourceLoaded(sourceId)) {
          onLayerLoaded(true);
          fitFirstMapToSourceBounds();
        }

        map.once('remove', () => {
          map.off('sourcedata', onSourceLoaded);
        });
      },
      [mapIndex, onLayerLoaded, onMapLoaded, sourceId]
    );

    return (
      <Map
        mapboxAccessToken={mapboxAccessToken}
        {...viewState}
        mapStyle={
          theme.palette.mode === 'light'
            ? mapboxMapStylesLinks.light
            : mapboxMapStylesLinks.dark
        }
        onMouseMove={throttledOnHover}
        onMouseDown={removeHoverInfo}
        onMouseOut={removeHoverInfo}
        onMoveStart={() => {
          onMoveStart(mapIndex);
        }}
        onMove={activeMap === mapIndex ? onMove : undefined}
        interactiveLayerIds={mapboxTileId ? [areaFillLayerId] : []}
        cursor={hasCursor ? 'pointer' : 'inherit'}
        ref={(el) => {
          mapsRefs.current[mapIndex] = el;
        }}
        onLoad={handleLoad}
      >
        {mapboxTileId && sourceLayer ? (
          <Source
            key={`${sourceId}-${sourceLayer}`}
            id={sourceId}
            type='vector'
            url={`mapbox://${mapboxTileId}`}
            promoteId='id'
          >
            <Layer
              id={areaFillLayerId}
              beforeId='waterway-label'
              type='fill'
              source={sourceId}
              source-layer={sourceLayer}
              paint={{ 'fill-color': bucketColorExpr }}
            />
            <Layer
              id={`comparison-map-boundary-${mapIndex.toString()}`}
              beforeId='waterway-label'
              type='line'
              source={sourceId}
              source-layer={sourceLayer}
              paint={{
                'line-width': 0.6,
                'line-color':
                  theme.palette.mode === 'light'
                    ? theme.palette.common.white
                    : theme.palette.common.black,
                'line-opacity': 0.8,
              }}
            />
          </Source>
        ) : null}

        {mapIndex === 0 ? (
          <MapControls
            mapRef={mapsRefs.current[mapIndex]}
            mapboxAccessToken={mapboxAccessToken}
            mapContainerId={COMPARISON_MAP_CONTAINER_ID}
          />
        ) : null}
      </Map>
    );
  }
);

ComparisonMapBody.displayName = 'ComparisonMapBody';

interface ComparisonMapProps {
  data: ComparisonMapItem[] | undefined;
  mapIndex: 0 | 1;
  tileInfo: { mapboxTileId: string | null; sourceLayer: string | null };
  viewState: ViewState;
  activeMap: number;
  mapsRefs: RefObject<(MapRef | null)[]>;
  onMove: (evt: ViewStateChangeEvent) => void;
  onMoveStart: (index: number) => void;
  hoverInfo: { feature: GeoJSONFeature; x: number; y: number } | null;
  throttledOnHover: (...args: unknown[]) => void;
  removeHoverInfo: () => void;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}

const ComparisonMap = ({
  data,
  mapIndex,
  tileInfo,
  viewState,
  activeMap,
  mapsRefs,
  onMove,
  onMoveStart,
  hoverInfo,
  throttledOnHover,
  removeHoverInfo,
  isLoading = false,
  isFetching = false,
  isError = false,
  onRetry,
}: ComparisonMapProps) => {
  const theme = useTheme();
  const { mapboxBucketColors } = useColorTokens();

  const { sourceLayer } = tileInfo;
  const sourceId = `${COMPARISON_MAP_SOURCE_PREFIX}${mapIndex.toString()}`;
  const colors = mapboxBucketColors.map1;

  const dataById = useMemo(
    (): Record<string, ComparisonMapItem> =>
      Object.fromEntries(data?.map((d) => [d.areaId, d]) ?? []),
    [data]
  );

  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isLayerLoaded, setIsLayerLoaded] = useState(false);

  useEffect(() => {
    setIsLayerLoaded(false);
  }, [sourceLayer]);

  useEffect(() => {
    if (!isMapLoaded || !isLayerLoaded || !sourceLayer || !data?.length) {
      return;
    }

    const mapRef = mapsRefs.current[mapIndex];
    if (!mapRef) {
      return;
    }
    const map = mapRef.getMap();
    if (!map.getSource(sourceId)) {
      return;
    }

    map.removeFeatureState({ source: sourceId, sourceLayer });

    for (const { areaId, areaName, bucket, populationSize } of data) {
      map.setFeatureState(
        { source: sourceId, sourceLayer, id: areaId },
        { name: areaName, bucket, populationSize }
      );
    }
  }, [
    data,
    isLayerLoaded,
    isMapLoaded,
    mapIndex,
    mapsRefs,
    sourceId,
    sourceLayer,
  ]);

  const bucketColorExpr = useMemo(
    () =>
      [
        'case',
        ['boolean', ['feature-state', 'hovered'], false],
        theme.palette.primary.main,
        [
          'match',
          ['feature-state', 'bucket'],
          'L',
          colors[0],
          'M',
          colors[1],
          'H',
          colors[2],
          'VH',
          colors[3],
          theme.palette.grey[300],
        ],
      ] as unknown as string,
    [colors, theme.palette.grey, theme.palette.primary.main]
  );

  return (
    <Box position='relative' height='100%'>
      <ComparisonMapBody
        mapIndex={mapIndex}
        tileInfo={tileInfo}
        viewState={viewState}
        activeMap={activeMap}
        mapsRefs={mapsRefs}
        onMove={onMove}
        onMoveStart={onMoveStart}
        throttledOnHover={throttledOnHover}
        removeHoverInfo={removeHoverInfo}
        hasCursor={!!hoverInfo}
        bucketColorExpr={bucketColorExpr}
        onMapLoaded={setIsMapLoaded}
        onLayerLoaded={setIsLayerLoaded}
      />

      <ComparisonMapTooltip
        position={hoverInfo ? { x: hoverInfo.x, y: hoverInfo.y } : null}
        item={
          hoverInfo
            ? (dataById[
                String(hoverInfo.feature.id ?? hoverInfo.feature.properties?.id)
              ] ?? null)
            : null
        }
      />

      <ComparisonMapLegend data={data} colors={colors} />

      <ComparisonMapOverlay
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRetry={onRetry ?? (() => undefined)}
      />
    </Box>
  );
};

export default ComparisonMap;
